import React, { useEffect, useRef, useState } from "react";
import { IoInformationCircleOutline } from "react-icons/io5";
import { ImSpinner2 } from "react-icons/im";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import DebounceInput from "react-debounce-input";
import { HiDotsHorizontal, HiOutlineShare } from "react-icons/hi";
import { HiOutlineClipboardDocument, HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";

import Modal from "../../../../../../components/modal";
import Loader from "../../../../../../components/loader";

import Info from "./info";
import Status from "./status";
import Raw from "./raw";
import Logs from "./logs";

import { copyToClipboard, classNames } from "../../../../../../utils";

import api from "../../../../../../services/api";

export default ({ onDelete, onUpdate, project }) => {
  const [tab, setTab] = useState("info");
  const [values, setValues] = useState();
  const [loading, setLoading] = useState(false);
  const { search } = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(search);

  useEffect(() => {
    const item_modal_id = query.get("item_modal_id");
    if (!values || values._id !== item_modal_id) fetch();
  }, [query]);

  const fetch = async () => {
    try {
      setValues(null);
      const item_modal_id = query.get("item_modal_id");
      if (!item_modal_id) return;
      setLoading(true);
      const res = await api.get(`/roadlog_item/${item_modal_id}`);
      if (!res.ok) return;
      setValues(res.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  if (!values) return <></>;

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        query.delete("item_modal_id");
        navigate({ pathname: location.pathname, search: query.toString() });
        setValues(null);
      }}>
      {loading ? (
        <Loader />
      ) : (
        <div className="w-full h-[90vh] bg-gray-100 overflow-y-scroll overflow-x-hidden">
          <div className=" bg-purple-50 flex flex-col w-full overflow-hidden shadow-sm rounded-t-md">
            <div className="flex justify-between items-center gap-3 pt-3 px-4 mr-4">
              <div className="text-gray-600 text-lg font-medium">{`# ${values.index}`}</div>
              <DebounceInput
                element="input"
                className="input text-base font-semibold flex-1 focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="Title..."
                id="title"
                value={values.title || ""}
                debounceTimeout={500}
                onChange={async (e) => {
                  const newTitle = e.target.value;
                  const updatedValues = { ...values, title: newTitle };
                  setValues(updatedValues);
                  await api.put(`/roadlog_item/${values._id}`, updatedValues);
                  toast.success("Title updated!");
                  onUpdate(updatedValues);
                }}
              />
              <button
                className="transparent-btn hover:bg-gray-100 rounded-md px-2 py-1 transition-colors"
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set("t", values.title);
                  copyToClipboard(url.href);
                  toast.success("Copied to clipboard!");
                }}>
                <HiOutlineShare className="mr-1" />
                <span className="text-xs">Share</span>
              </button>
              <Menu
                item={values}
                setItem={(v) => {
                  setValues(v);
                }}
                onDelete={() => onDelete(values)}
              />
            </div>
            <nav className="flex flex-row w-full mt-2 bg-purple-50 px-2 pt-1 border-b border-gray-200">
              <TabItem title="Info" tab="info" setTab={setTab} active={tab === "info"} Icon={IoInformationCircleOutline} />
              <TabItem title="Logs" tab="logs" setTab={setTab} active={tab === "logs"} Icon={HiOutlineClipboardDocument} />
              <TabItem title="Status" tab="status" setTab={setTab} active={tab === "status"} Icon={HiOutlineAdjustmentsHorizontal} />
              <TabItem title="Raw" tab="raw" setTab={setTab} active={tab === "raw"} />
            </nav>
          </div>
          {tab === "info" && <Info item={values} setItem={(e) => setValues(e)} project={project} onUpdate={onUpdate} />}
          {tab === "raw" && <Raw item={values} />}
          {tab === "status" && <Status item={values} setItem={setValues} />}
          {tab === "logs" && <Logs item={values} />}
        </div>
      )}
    </Modal>
  );
};

const TabItem = ({ tab, title, setTab, active, Icon, Badge }) => (
  <button
    onClick={() => setTab(tab)}
    className={classNames(
      "group flex items-center px-3 py-1.5 text-sm font-medium rounded-t-md transition-all duration-200",
      active ? "text-blue-600 bg-white border-t border-l border-r border-gray-200 shadow-sm -mb-px" : "text-gray-600 hover:text-blue-500 hover:bg-gray-100",
    )}>
    {Icon ? (
      <Icon className={classNames(active ? "text-blue-600" : "text-gray-500 group-hover:text-blue-500", "h-4 w-4 transition-colors duration-200 mr-1.5")} aria-hidden="true" />
    ) : null}
    <span>{title}</span>
    {Badge ? Badge : null}
  </button>
);

const Menu = ({ item, setItem, onDelete }) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(search);
  const [open, setOpen] = useState(false);
  const menuRef = useRef();

  useEffect(
    function () {
      if (!open) return;
      function handleClickOutside(e) {
        if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
      }
      document.addEventListener("mousedown", handleClickOutside);
      return function () {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    },
    [open],
  );

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this ticket?")) return;
    try {
      const { ok } = await api.remove(`/roadlog_item/${item._id}`);
      if (!ok) return toast.error("Something went wrong!");
      toast.success("Item deleted!");

      query.delete("item_modal_id");
      navigate({ pathname: location.pathname, search: query.toString() });
      onDelete(item);
      setItem(null);
    } catch (e) {
      console.log(e);
      toast.error("Something went wrong!");
    }
  };

  const handleAddToBacklog = async () => {
    const { ok } = await api.put(`/roadlog_item/${item._id}`, {
      status: "TODO",
      is_in_backlog: true,
    });
    if (!ok) return toast.error("Something went wrong!");
    toast.success("Item added to backlog!");
  };

  return (
    <div className="relative" ref={menuRef}>
      <button className="transparent-btn" onClick={() => setOpen((p) => !p)}>
        <HiDotsHorizontal />
      </button>
      {open && (
        <div className="text-sm fixed right-2 top-[3rem] bg-white border border-gray-200 rounded-md shadow-md min-w-40 z-40">
          <button className="flex items-center gap-2 px-4 py-2 w-full hover:bg-gray-100" onClick={() => handleAddToBacklog()}>
            <span>Add to backlog</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 w-full hover:bg-gray-100" onClick={handleDelete}>
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
};
